import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.string.small.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import { useTheme } from 'styled-components';
import useBreakpoints from 'hooks/useBreakpoints';
import { getColorBlock } from 'utils/ui/colors';
import { remToPx } from 'utils/ui';
import { jsx as _jsx } from "react/jsx-runtime";
function BarChart(_ref) {
  var data = _ref.data,
    label = _ref.label,
    ariaLabel = _ref.ariaLabel,
    labels = _ref.labels,
    _ref$color = _ref.color,
    color = _ref$color === void 0 ? 'chart' : _ref$color,
    nullLabel = _ref.nullLabel,
    _ref$showLegend = _ref.showLegend,
    showLegend = _ref$showLegend === void 0 ? false : _ref$showLegend,
    _ref$animation = _ref.animation,
    animation = _ref$animation === void 0 ? false : _ref$animation,
    onXLabelClick = _ref.onXLabelClick;
  var canvasRef = useRef(null);
  var chartRef = useRef(null);
  var _useBreakpoints = useBreakpoints(),
    isDesktop = _useBreakpoints.s;
  var theme = useTheme();
  var chartColor = getColorBlock(color, theme);
  var textColor = theme.text;
  var linkColor = theme.links;
  var isEmpty = data.length === 0 || labels.length === 0;
  useEffect(function () {
    var canvas = canvasRef.current;
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    var chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: label,
          data: data,
          backgroundColor: chartColor,
          borderRadius: theme.globals.borderRadius.small,
          barPercentage: 0.7,
          categoryPercentage: 0.9
        }]
      },
      options: {
        animation: animation,
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            beginAtZero: true,
            grid: {
              display: false
            },
            ticks: {
              autoSkip: false,
              color: onXLabelClick ? linkColor : textColor,
              font: {
                family: 'Open Sans'
              }
            },
            border: {
              color: chartColor
            }
          },
          y: {
            min: 0,
            max: 100,
            ticks: {
              stepSize: 20,
              // Forces steps of 20
              autoSkip: false,
              callback: function callback(val) {
                return "".concat(val, "%");
              },
              color: textColor,
              font: {
                family: 'Open Sans'
              }
            },
            border: {
              color: chartColor
            },
            grid: {
              display: true
            }
          }
        },
        // This is used to ensure that tooltips are displayed even if the data value is null
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: showLegend
          },
          tooltip: {
            displayColors: false,
            titleColor: theme.background,
            titleFont: {
              weight: 'bold',
              size: remToPx(theme.globals.typeScale.s),
              family: 'Open Sans'
            },
            bodyColor: theme.background,
            bodyFont: {
              size: remToPx(theme.globals.typeScale.s),
              family: 'Open Sans'
            },
            backgroundColor: theme.text,
            borderWidth: 0,
            cornerRadius: theme.globals.borderRadius.small,
            callbacks: {
              label: function label(tooltipItem) {
                var value = tooltipItem.raw === null ? nullLabel : "".concat(tooltipItem.raw, "%");
                return value;
              }
            }
          }
        }
      }
    });
    chartRef.current = chart;
    var handleClick = function handleClick(event) {
      if (!chart || !onXLabelClick) return;
      var xScale = chart.scales.x;
      var clickX = event.offsetX;
      var pixelMargin = 30;
      xScale.ticks.forEach(function (tick, i) {
        var pixel = xScale.getPixelForTick(i);
        if (Math.abs(pixel - clickX) < pixelMargin) {
          var labelAtIndex = labels[i];
          if (labelAtIndex) {
            onXLabelClick(labelAtIndex);
          }
        }
      });
    };
    var handleMouseMove = function handleMouseMove(event) {
      if (!chart || !onXLabelClick) return;
      var xScale = chart.scales.x;
      var hoverX = event.offsetX;
      var pixelMargin = 30;
      var hovering = false;
      xScale.ticks.forEach(function (tick, i) {
        var pixel = xScale.getPixelForTick(i);
        if (Math.abs(pixel - hoverX) < pixelMargin) {
          hovering = true;
        }
      });
      canvas.style.cursor = hovering ? 'pointer' : 'default';
    };
    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('mousemove', handleMouseMove);
    return function () {
      chart.destroy();
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [data, labels, color, isDesktop, isEmpty, theme, animation, nullLabel, ariaLabel, label, showLegend, chartColor, textColor, onXLabelClick, linkColor]);
  return /*#__PURE__*/_jsx("canvas", {
    ref: canvasRef,
    role: "img",
    "aria-label": ariaLabel
  });
}
export default BarChart;